// src/components/admin/AdminApprovals.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Layout,
  Table,
  Button,
  Card,
  Typography,
  Modal,
  message,
  Tag,
  Space,
  Input,
  Row,
  Col,
  Select,
} from "antd";
import dayjs from "dayjs";
import AdminHeader from "./AdminHeader";

const { Content } = Layout;
const { Title } = Typography;
const { Search } = Input;
const { Option } = Select;

export default function AdminApprovals() {
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(false);
  const [processingId, setProcessingId] = useState(null);

  const [messageApi, messageContextHolder] = message.useMessage();
  const [modal, modalContextHolder] = Modal.useModal();

  const apiBase = (
  window.__APP_CONFIG__?.REACT_APP_API_BASE_URL ||
  process.env.REACT_APP_API_BASE_URL ||
  "http://localhost:8000"
).replace(/\/$/, ""); // remove trailing slash if any
  const listEndpoint = apiBase + "/list_pending.php";
  const approveEndpoint = apiBase + "/approve.php";

  // === Public Upload Base URL ===
// Used for displaying profile photos or fallback images
const uploadBase = (
  window.__APP_CONFIG__?.UPLOAD_BASE_URL ||
  process.env.REACT_APP_UPLOAD_BASE_URL ||
  // fallback: derive from apiBase
  (apiBase.replace(/\/backend\/?$/i, '') + '/uploads')
).replace(/\/$/, '');


  const token = localStorage.getItem("admin_token") || null;

  // Table state: pagination, sorter, search
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const [sorter, setSorter] = useState({ field: null, order: null });
  const [searchText, setSearchText] = useState("");
  const searchRef = useRef(null);
  const debounceRef = useRef(null);

  const fetchPending = async () => {
    setLoading(true);
    try {
      const res = await fetch(listEndpoint, {
        method: "GET",
        headers: { Accept: "application/json" },
      });
      const data = await res.json();
      if (!res.ok || !data.ok) {
        throw new Error(data.error || `Failed to fetch pending records (status ${res.status})`);
      }
      setPending(data.pending || []);
    } catch (err) {
      console.error("fetch pending error", err);
      messageApi.error(err?.message || "Failed to load pending list");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPending();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const confirmApprove = (userId) => {
    if (!token) {
      modal.confirm({
        title: "Not logged in",
        content: "You must be logged in as an admin to approve users. Please sign in via Admin → Login.",
        okText: "Go to Login",
        cancelText: "Cancel",
        onOk: () => window.scrollTo({ top: 0, behavior: "smooth" }),
      });
      return;
    }

    modal.confirm({
      title: "Approve user",
      content: "Are you sure you want to approve this user? This will notify the user via email.",
      okText: "Approve",
      cancelText: "Cancel",
      onOk: async () => {
        await doApprove(userId);
      },
    });
  };

  const doApprove = async (userId) => {
    setProcessingId(userId);
    try {
      const fd = new FormData();
      fd.append("user_id", String(userId));
      fd.append("action", "APPROVE");

      const res = await fetch(approveEndpoint, {
        method: "POST",
        body: fd,
        headers: { Authorization: "Bearer " + token },
      });

      const raw = await res.text();
      let data = {};
      try {
        data = raw ? JSON.parse(raw) : {};
      } catch {
        console.warn("approve: non-JSON response:", raw);
      }

      if (!res.ok) throw new Error(data.error || data.message || `Approve failed (status ${res.status})`);

      messageApi.success("User approved");
      await fetchPending();
    } catch (err) {
      console.error("approve error", err);
      messageApi.error(err?.message || "Approve failed");
    } finally {
      setProcessingId(null);
    }
  };

  // New: confirmReject shows a dialog with comment box; the comment is passed to doReject
  const confirmReject = (userId) => {
    // Build a unique ID for the textarea so we can read its value on OK
    const textareaId = `reject-reason-${userId}-${Date.now()}`;

    modal.confirm({
      title: "Reject user",
      width: 560,
      content: (
        <div>
          <div style={{ marginBottom: 8 }}>
            Reject this registration? You may optionally add a short note that will be sent to the user.
          </div>
          <Input.TextArea
            id={textareaId}
            placeholder="Optional rejection note (e.g. incomplete documents, incorrect details)."
            autoSize={{ minRows: 3, maxRows: 6 }}
            aria-label="Rejection note"
          />
        </div>
      ),
      okText: "Reject",
      cancelText: "Cancel",
      onOk: async () => {
        // read the value by id (safe simple approach)
        const el = document.getElementById(textareaId);
        const note = el ? (el.value || "").trim() : "";
        await doReject(userId, note);
      },
    });
  };

  // Updated doReject to accept optional note and include it in the request payload
  const doReject = async (userId, note = "") => {
    if (!token) {
      messageApi.error("You must be logged in as admin to reject.");
      return;
    }
    setProcessingId(userId);
    try {
      const fd = new FormData();
      fd.append("user_id", String(userId));
      fd.append("action", "REJECT");
      if (note) fd.append("note", note);

      const res = await fetch(approveEndpoint, {
        method: "POST",
        body: fd,
        headers: { Authorization: "Bearer " + token },
      });

      const raw = await res.text();
      let data = {};
      try {
        data = raw ? JSON.parse(raw) : {};
      } catch {}

      if (!res.ok) throw new Error(data.error || `Reject failed (status ${res.status})`);

      messageApi.success("User rejected");
      await fetchPending();
    } catch (err) {
      console.error("reject error", err);
      messageApi.error(err?.message || "Reject failed");
    } finally {
      setProcessingId(null);
    }
  };

  // Handle search input with debounce
  const onSearchChange = (value) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSearchText(value.trim().toLowerCase());
      setPagination((p) => ({ ...p, current: 1 })); // reset to page 1 on new search
    }, 300);
  };

  // Handle table change (pagination, sorting)
  const handleTableChange = (newPagination, filters, newSorter) => {
    setPagination({ current: newPagination.current, pageSize: newPagination.pageSize });
    if (newSorter && newSorter.field) {
      setSorter({ field: newSorter.field, order: newSorter.order });
    } else {
      setSorter({ field: null, order: null });
    }
  };

  // Compute filtered and sorted data
  const processedData = useMemo(() => {
    let data = Array.isArray(pending) ? [...pending] : [];

    // search filter
    if (searchText) {
      data = data.filter((d) => {
        const hay = [
          d.first_name,
          d.last_name,
          d.email,
          d.roll_number,
          d.city,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return hay.includes(searchText);
      });
    }

    // sorting (client-side)
    if (sorter && sorter.field) {
      const { field, order } = sorter;
      data.sort((a, b) => {
        let av = a[field];
        let bv = b[field];

        // support nested or derived fields
        if (field === "name") {
          av = `${a.first_name || ""} ${a.last_name || ""}`.trim();
          bv = `${b.first_name || ""} ${b.last_name || ""}`.trim();
        } else if (field === "created_at") {
          av = a.created_at ? new Date(a.created_at).getTime() : 0;
          bv = b.created_at ? new Date(b.created_at).getTime() : 0;
        } else {
          av = av ?? "";
          bv = bv ?? "";
        }

        if (typeof av === "string") av = av.toLowerCase();
        if (typeof bv === "string") bv = bv.toLowerCase();

        if (av < bv) return order === "ascend" ? -1 : 1;
        if (av > bv) return order === "ascend" ? 1 : -1;
        return 0;
      });
    }

    return data;
  }, [pending, searchText, sorter]);

  // Slice data for pagination
  const pagedData = useMemo(() => {
    const { current, pageSize } = pagination;
    const start = (current - 1) * pageSize;
    return processedData.slice(start, start + pageSize);
  }, [processedData, pagination]);

  const total = processedData.length;

  const columns = [
    {
      title: "Profile",
      dataIndex: "first_name",
      key: "profile",
      width: 300,
      render: (_, record) => (
        <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
          <img
  src={
    record.profile_photo_url
      ? record.profile_photo_url
      : `${uploadBase}/avatar-placeholder.png`
  }
  alt={`${record.first_name || ''} ${record.last_name || ''}`.trim() || 'avatar'}
  style={{
    width: 48,
    height: 48,
    objectFit: "cover",
    borderRadius: 6,
    border: "1px solid #eee",
  }}
  onError={(e) => {
    try {
      const placeholder = `${uploadBase}/avatar-placeholder.png`;
      // if current src is already the placeholder, switch to bundled asset as last-resort
      if (e.currentTarget.src && e.currentTarget.src.endsWith('avatar-placeholder.png')) {
        e.currentTarget.src = '/assets/avatar-placeholder.png';
      } else {
        e.currentTarget.src = placeholder;
      }
    } catch {
      e.currentTarget.src = '/assets/avatar-placeholder.png';
    }
  }}
/>
          <div>
            <div style={{ fontWeight: 600 }}>
              {record.first_name} {record.last_name}
            </div>
            <div style={{ fontSize: 12, color: "#6b7280" }}>{record.email}</div>
          </div>
        </div>
      ),
    },
    {
      title: "Roll / City",
      dataIndex: "roll_number",
      key: "roll",
      width: 180,
      render: (text, r) => (
        <div>
          <div>{text || "-"}</div>
          <div style={{ fontSize: 12, color: "#6b7280" }}>{r.city || "-"}</div>
        </div>
      ),
    },
    {
      title: "Submitted",
      dataIndex: "created_at",
      key: "created_at",
      sorter: true,
      width: 180,
      render: (t) => (t ? dayjs(t).format("YYYY-MM-DD HH:mm") : "-"),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 140,
      render: (s) => <Tag color={s === "UNDER_REVIEW" ? "orange" : "green"}>{s}</Tag>,
      filters: [
        { text: "UNDER_REVIEW", value: "UNDER_REVIEW" },
        { text: "APPROVED", value: "APPROVED" },
        { text: "REJECTED", value: "REJECTED" },
      ],
      onFilter: (value, record) => record.status === value,
    },
    {
      title: "Actions",
      key: "actions",
      width: 180,
      render: (_, rec) => (
        <Space>
          <Button
            type="primary"
            size="small"
            loading={processingId === rec.id}
            onClick={() => confirmApprove(rec.id)}
            disabled={processingId !== null}
          >
            Approve
          </Button>
          <Button
            danger
            size="small"
            loading={processingId === rec.id}
            onClick={() => confirmReject(rec.id)}
            disabled={processingId !== null}
          >
            Reject
          </Button>
        </Space>
      ),
    },
  ];

  // Table onChange handler wrapper (AntD's Table expects this)
  const onTableChange = (newPagination, filters, newSorter) => {
    // handle sort
    const sortField = newSorter?.field || null;
    const sortOrder = newSorter?.order || null;
    setSorter({ field: sortField, order: sortOrder });
    // handle pagination
    setPagination({ current: newPagination.current, pageSize: newPagination.pageSize });
  };

  // Utility: quick refresh
  const refresh = () => {
    fetchPending();
  };

  return (
    <Layout style={{ minHeight: "100vh", background: "#f5f6fa" }}>
      <AdminHeader />

      <Content style={{ padding: "24px 40px" }}>
        {messageContextHolder}
        {modalContextHolder}

        <Card
          bordered={false}
          style={{
            width: "100%",
            boxShadow: "0 4px 10px rgba(0,0,0,0.04)",
            borderRadius: 12,
            background: "#fff",
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
            <Title level={4} style={{ marginBottom: 0 }}>
              Pending Alumni Approvals
            </Title>

            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <Search
                placeholder="Search name, email, roll, city..."
                allowClear
                onChange={(e) => onSearchChange(e.target.value)}
                style={{ width: 320 }}
                ref={searchRef}
              />

              <Select
                value={pagination.pageSize}
                onChange={(ps) => setPagination((p) => ({ ...p, pageSize: ps, current: 1 }))}
                style={{ width: 110 }}
              >
                <Option value={10}>10 / page</Option>
                <Option value={20}>20 / page</Option>
                <Option value={50}>50 / page</Option>
              </Select>

              <Button onClick={refresh} loading={loading}>
                Refresh
              </Button>
            </div>
          </div>

          <Table
            rowKey="id"
            columns={columns}
            dataSource={pagedData}
            loading={loading}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total,
              showSizeChanger: false,
              showTotal: (t) => `Total ${t} records`,
            }}
            style={{ marginTop: 16, width: "100%" }}
            scroll={{ x: true }}
            locale={{ emptyText: "No pending registrations" }}
            onChange={onTableChange}
          />
        </Card>
      </Content>
    </Layout>
  );
}
